import { Component, ElementRef, forwardRef, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { INPUT_SIZE, INPUT_THEME, INPUT_TYPE } from '.';
import { LanguageService } from '../../services/parameters';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements OnInit, ControlValueAccessor {
  @ViewChild('mtInput', { static: false }) public input: ElementRef;

  @Input() public maxCharLength: number;
  @Input() public minCharLength: number;
  @Input() public label: string;
  @Input() public placeholder: string;
  // @Input() public errorMessage: string = '';
  @Input() public type: 'text' | 'email' | 'number' | 'password' = 'text';
  @Input() public inputType: INPUT_TYPE;
  @Input() public displayIcon: boolean;
  @Input() public requiredField: boolean;
  @Input() public theme: INPUT_THEME;
  @Input() public size: INPUT_SIZE;
  @Input() public tooltipDescription;
  @Input() public tagPrepend: boolean;
  @Input() public tagSmall: boolean;
  @Input() public clearable: boolean;
  @Input() public charRestriction: RegExp;
  @Input() public isDisabled: boolean;

  private _errorMessage: string;
  @Input()
  public set errorMessage(value: string) {
    this._errorMessage = value;
  }
  public get errorMessage(): string {
    return this._errorMessage;
  }

  public value = '';
  public imageType: string;
  public imageKey: string;
  public isActive: boolean;
  public cursor: boolean;
  public INPUT_THEME = INPUT_THEME;
  public INPUT_TYPE = INPUT_TYPE;

  public max = Math.max;

  onChange = (_: any) => {};
  onTouch = () => {};

  constructor(private languageService: LanguageService, private renderer: Renderer2) {
    this.placeholder = '';
    this.isActive = false;
    this.size = INPUT_SIZE.NORMAL;
  }

  ngOnInit() {
    this.inputHandler();
  }

  writeValue(value: any): void {
    this.value = value || '';
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInput(value: any) {
    if (value && !this.tagPrepend) {
      const maxCharMessage = this.languageService.message('max-characters');
      // if get an errorMessage from input then display, else validate maxCharLength
      if (!(this.errorMessage && this.errorMessage !== maxCharMessage)) {
        this.errorMessage = null;
        if (this.maxCharLength && value.value.length >= this.maxCharLength && !this.errorMessage) {
          this.errorMessage = maxCharMessage;
        }
      }
    }
    this.value = value.value;
    this.onTouch();
    this.onChange(this.value);
  }

  onTouched() {}

  /**
   * define if input is password or isnt, and change input type property
   *
   * @param input Input object
   */
  public clickIcon(input: HTMLInputElement): void {
    this.isActive = !this.isActive;

    if (this.inputType === INPUT_TYPE.PASSWORD) {
      input.type = input.type === 'password' ? 'text' : 'password';
      this.setPasswordIcon();
    }
    if (this.inputType === INPUT_TYPE.SEARCH) {
      if (this.value !== '') {
        this.writeValue(this.value);
      }
    }
  }

  /**
   * Handle the icon of the input type according to the type
   */
  private inputHandler(): void {
    if (this.inputType === INPUT_TYPE.PASSWORD) {
      this.setPasswordIcon();
    } else if (this.inputType === INPUT_TYPE.SEARCH) {
      this.setSearchIcon();
      this.setSearchPlaceHolder();
    }
  }

  /**
   * Set password icon from open eye inactive to close eye active
   */
  private setPasswordIcon(): void {
    if (this.displayIcon) {
      this.cursor = true;
      if (this.isActive) {
        this.imageType = 'icon-active';
        this.imageKey = 'close-eye';
      } else {
        this.imageType = 'icon-inactive';
        this.imageKey = 'open-eye';
      }
    }
  }

  /**
   * Set search icon if this.displayIcon is true
   */
  private setSearchIcon(): void {
    if (this.displayIcon) {
      this.imageType = 'icon';
      this.imageKey = 'search';
    }
  }

  /**
   * Set search placeholder if has placeholder from inputs
   */
  private setSearchPlaceHolder(): void {
    if (!this.placeholder) {
      this.placeholder = `${this.languageService.message('search')}...`;
    }
  }

  public clearInput(): void {
    this.value = '';
    this.errorMessage = null;
    this.onTouch();
    this.onChange(this.value);
  }
}
